import { useSyncExternalStore } from "react";

export type TenantConfig = {
    id: string | null;
    name: string;
    logoUrl: string;
    logoWordmarkUrl: string;
    locale: string;
    currency: string;
    websiteUrl: string;
    websiteSite: string;
    /** Endereço público do site do tenant (domínio próprio verificado ou subdomínio da plataforma). */
    siteUrl: string;
    /** Endereço que o editor visual abre no iframe: sempre o subdomínio, que o CSP libera. */
    sitePreviewUrl: string;
};

const DEFAULT_WEBSITE_URL = process.env.NEXT_PUBLIC_WEBSITE_URL || "https://7days.pt";

/** URL do site em modo de edição (`?edit=true`), sem barra dupla quando o URL já termina em `/`. */
export function siteEditUrl(siteUrl: string): string {
    const url = new URL(siteUrl);
    url.searchParams.set("edit", "true");
    return url.toString();
}

export const DEFAULT_TENANT: TenantConfig = Object.freeze({
    id: process.env.NEXT_PUBLIC_TENANT_ID || null,
    name: process.env.NEXT_PUBLIC_TENANT_NAME || "7Days",
    logoUrl: "/image/logo.png",
    logoWordmarkUrl: "/image/logo2.webp",
    locale: process.env.NEXT_PUBLIC_LOCALE || "pt-PT",
    currency: process.env.NEXT_PUBLIC_DEFAULT_CURRENCY || "EUR",
    websiteUrl: DEFAULT_WEBSITE_URL,
    websiteSite: new URL(DEFAULT_WEBSITE_URL).host,
    siteUrl: DEFAULT_WEBSITE_URL,
    sitePreviewUrl: DEFAULT_WEBSITE_URL,
});

let current: TenantConfig = DEFAULT_TENANT;
const listeners = new Set<() => void>();

export function getTenant(): TenantConfig {
    return current;
}

/** Campos ausentes mantêm o valor padrão; trocar de tenant exige resetar a sessão do cliente. */
export function setTenant(patch: Partial<TenantConfig>): void {
    current = { ...DEFAULT_TENANT, ...patch };
    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

const getServerSnapshot = () => DEFAULT_TENANT;

export function useTenant(): TenantConfig {
    return useSyncExternalStore(subscribe, getTenant, getServerSnapshot);
}

/**
 * Prefixo das chaves do navegador (tokens, permissões). Usa só o tenant fixado no build
 * (`NEXT_PUBLIC_TENANT_ID`), nunca o da marca carregada em runtime: essa chega depois do boot,
 * e a sessão gravada com ela não era encontrada ao recarregar, o que fazia a troca de
 * organização voltar à anterior sem fim.
 */
export function tenantStorageKey(key: string): string {
    return DEFAULT_TENANT.id ? `${DEFAULT_TENANT.id}:${key}` : key;
}

